import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { EmailCodeSignIn } from "@dolfin/ui";
import { useAuth, type OAuthProvider } from "../auth/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { GoogleIcon } from "@/components/icons/GoogleIcon";
import { Spinner } from "@/components/ui/spinner";
import { useTheme } from "@/components/ThemeProvider";
import logoLight from "../logo-light.svg";
import logoDark from "../logo-dark.svg";
import { SESSION_STORAGE_KEY, ACTIVE_CLIENT_KEY } from "@/lib/constants";
import type { components } from "@dolfin/api-admin";

type AcceptResponse = components["schemas"]["AuthResponse"];

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const logo = theme === "dark" ? logoDark : logoLight;

  const token = searchParams.get("token");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  // A Demo client is said out loud before entering, so nobody takes its made-up data for a real client's.
  const [joinedDemo, setJoinedDemo] = useState<string | null>(null);

  // Even when signed in, require Google or an email code, so the verified email is checked against the invite.
  const invitedEmail = token ? invitedEmailOf(token) : null;

  // Resolves to an error to show, or null once accepted.
  const accept = async (accessToken: string, provider: OAuthProvider): Promise<string | null> => {
    if (!token) return null;
    setError(null);
    setBusy(true);
    try {
      const resp = await fetch("/api/proxy/v1/client-invites/accept", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, accessToken, provider }),
      });

      if (!resp.ok) {
        const problem = await resp.json().catch(() => null);
        if (resp.status === 403) {
          return problem?.detail ?? "The invite was sent to a different email address than the one you signed in with.";
        }
        if (resp.status === 401) {
          return problem?.detail ?? "This invite is invalid or expired. Ask for a new one.";
        }
        return problem?.detail ?? "Couldn't accept the invite.";
      }

      const data: AcceptResponse = await resp.json();
      // The response lists every client the user has; the one just joined is the one the old session lacked.
      const before = new Set<string>(
        (() => {
          try {
            return JSON.parse(localStorage.getItem(SESSION_STORAGE_KEY) ?? "null")?.user?.clients?.map((c: { clientId: string }) => c.clientId) ?? [];
          } catch {
            return [];
          }
        })(),
      );
      const joined = data.clients.find((c) => !before.has(c.clientId)) ?? (data.clients.length === 1 ? data.clients[0] : undefined);
      // Stash the new session directly so AuthProvider picks it up on reload
      const session = {
        token: data.token,
        user: {
          userId: data.userId,
          email: data.email,
          organisations: data.organisations,
          clients: data.clients,
        },
        activeClientId: joined?.clientId ?? data.clients[0]?.clientId ?? "",
      };
      localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
      if (session.activeClientId) {
        localStorage.setItem(ACTIVE_CLIENT_KEY, session.activeClientId);
      }
      if (joined?.type === "Demo") setJoinedDemo(joined.clientName);
      else setDone(true);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "implicit",
    onSuccess: async (tokenResponse) => setError(await accept(tokenResponse.access_token, "google")),
    onError: () => {
      setError("Google sign-in failed. Please try again.");
      setBusy(false);
    },
  });

  const signInOptions = (
    <div className="flex w-full flex-col gap-2">
      <Button variant="outline" size="lg" className="w-full" onClick={() => googleLogin()} disabled={busy}>
        <GoogleIcon /> Continue with Google
      </Button>
      {token && (
        <EmailCodeSignIn
          inviteToken={token}
          email={invitedEmail ?? undefined}
          label={invitedEmail ? `Email a code to ${invitedEmail}` : "Continue with email"}
          onProof={async (proof) => {
            // The spinner card unmounts this component while accepting, so the page shows the error.
            const failure = await accept(proof, "email");
            setError(failure);
            return failure;
          }}
        />
      )}
    </div>
  );

  // After accept, hard-reload into the app so AuthProvider rehydrates from localStorage.
  useEffect(() => {
    if (done) {
      window.location.replace("/");
    }
  }, [done]);

  if (!token) {
    return (
      <CenterCard logo={logo} title="Invalid invite link">
        <p className="text-sm text-muted-foreground">
          This URL is missing the invite token. Ask your inviter to send you a fresh link.
        </p>
      </CenterCard>
    );
  }

  if (joinedDemo) {
    return (
      <CenterCard
        logo={logo}
        title="You've joined a Dolfin demo client"
        description={`${joinedDemo} is a demo: its connections are simulated and all of its data is made up.`}
      >
        <Button size="lg" className="w-full" onClick={() => window.location.replace("/")}>
          Continue
        </Button>
      </CenterCard>
    );
  }

  if (isAuthenticated && !done && !busy) {
    return (
      <CenterCard logo={logo} title="Accept invite" description="Confirm with Google or an email code to add this client to your portal account.">
        {signInOptions}
        {error && <p className="text-sm text-destructive text-center">{error}</p>}
      </CenterCard>
    );
  }

  if (busy) {
    return (
      <CenterCard logo={logo} title="Accepting invite…">
        <div className="flex justify-center"><Spinner className="size-6" /></div>
      </CenterCard>
    );
  }

  if (done) return <Navigate to="/" replace />;

  return (
    <CenterCard logo={logo} title="You're invited to Dolfin Portal" description="Sign in with Google or an email code, using the email this invite was sent to.">
      {signInOptions}
      {error && <p className="text-sm text-destructive text-center">{error}</p>}
    </CenterCard>
  );
}

function CenterCard({
  logo, title, description, children,
}: {
  logo: string;
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center gap-4 text-center">
          <img src={logo} alt="Dolfin" className="mx-auto h-16 w-16" />
          <CardTitle className="text-2xl">{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {children}
        </CardContent>
      </Card>
    </div>
  );
}

// Display only: the backend checks the signed token. Unparseable means no prefill.
function invitedEmailOf(token: string): string | null {
  try {
    const payload = token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), "=")));
    return typeof claims.invitedEmail === "string" ? claims.invitedEmail : null;
  } catch {
    return null;
  }
}
